import { Component, DestroyRef, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { httpsCallable } from 'firebase/functions';
import localforage from 'localforage';
import { BaseState } from '../../reducers';
import { Store } from '@ngrx/store';
import {  selectUserState } from '../../users/store/user.selector';
import { filter, map, take ,interval, withLatestFrom, distinctUntilChanged, pairwise, firstValueFrom, switchMap, EMPTY, delay } from 'rxjs'; import { FirebaseApp } from '@angular/fire/app';
import { HttpClient } from '@angular/common/http';
import { Firestore } from '@angular/fire/firestore';
import { FirebaseFirestoreService } from '../../services/firebase-firestore.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FirebaseRenderData, GroupRenderData } from '../../services/firebase.model';
import { RenderGridComponent } from '../../editor/render-grid/render-grid.component';
import { FirebaseFunctionService } from '../../services/firebase-function.service';
import objectHash from 'object-hash';
import { selectLayerOrder, selectLayers, selectRenderName } from '../../editor/store/editor.selector';
import { firebaseDiffObjects } from '../../editor/store/editor.utils';
import { RawImageDataLoaderService } from '../../services/image-loader.service';
import { RenderDataActionsService } from '../../services/render-data-actions.service';
import { defaultEditorState, resetEditorState, setRender } from '../../editor/store/editor.actions';
import { ImageDrawService } from '../../services/image-draw.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

enum LoadingState {
  Idle = "Idle",
  Downloading = "Downloading",
  Decompressing = "Decompressing"
}

@Component({
  selector: 'app-enterprise-editor-container',
  standalone: true,
  imports: [RenderGridComponent, ProgressSpinnerModule],
  templateUrl: './enterprise-editor-container.component.html',
  styleUrl: './enterprise-editor-container.component.scss'
})
export class EnterpriseEditorContainerComponent implements OnDestroy {
  firebaseApp = inject(FirebaseApp)
  firestore = inject(Firestore)
  imageDrawService = inject(ImageDrawService)

  functions = inject(FirebaseFunctionService)

  userState$ = this.store.select(selectUserState)
  loading = true
  destroy = inject(DestroyRef)
  loadingState: LoadingState = LoadingState.Idle

  renderDataSnapshot$ = this.userState$.pipe(
    delay(100),
    switchMap(userState => {
      if (!userState || !userState.groupId){
        return EMPTY
      }
      const renderId = this.activatedRoute.snapshot.params['id']
      if (!renderId) {
        return EMPTY
      }
      return this.firestoreService.getGroupRenderData(userState.groupId, renderId)
    })
  )

  loadRender$ = this.userState$.pipe(
    filter((state) => !!state?.groupId),
    take(1),
    map(async (userData): Promise<{buf: ArrayBuffer, renderData: GroupRenderData, groupId: string}> => {
      if (!userData?.groupId) {
        throw "User does not belong to enterprise group"
      }
      const groupId = userData.groupId
      this.loadingState = LoadingState.Downloading
      const renderId = this.activatedRoute.snapshot.params['id']
      const renderData = await firstValueFrom(this.firestoreService.getGroupRenderData(userData.groupId ,renderId).pipe(
        filter(renderData => !!renderData)
      )) as GroupRenderData
      const baseDataId = renderData.baseDataId
      const localRender = await localforage.getItem<ArrayBuffer>(`groupRender/${baseDataId}`)
      if (localRender !== null) {
        return {buf: localRender, renderData, groupId}
      }

      const url = await httpsCallable(this.functions.functions, 'getGroupFile')({
        groupId: userData.groupId,
        renderId: baseDataId,
        fileType: "render"
      })


      return new Promise((resolve) => {
        this.httpClient.get(url.data as string, {responseType: 'arraybuffer'}).subscribe((blob) => {
          localforage.setItem(`groupRender/${baseDataId}`, blob).then(() => {
            resolve({buf: blob, renderData, groupId })
          })
        })
      })
    }),

    map(async (result) => {
      const data = await result
      this.loadingState = LoadingState.Decompressing
      await this.imageDataService.loadImageFromFile(data.buf)
      this.renderDataActionService.setGroupRenderData(data.renderData, data.groupId)
      this.loadingState = LoadingState.Idle
      this.loading = false
      return true
    })
  )

  async ngOnDestroy() {
    const renderData = await firstValueFrom(this.renderDataSnapshot$)
    const userState = await firstValueFrom(this.userState$)
    if (!renderData || !userState) {
      return
    }

    const thumbnail = await this.imageDrawService.createThumbnailImage(
      renderData.editorState,
      true
    )

    httpsCallable(this.functions.functions, "uploadGroupThumbnail")(
      {
        groupId: userState.groupId,
        renderId: renderData.id,
        thumbnail
      }
    )
    this.store.dispatch(resetEditorState())
  }

  syncSave = interval(100).pipe(
    withLatestFrom(
      this.store.select(selectLayers),
      this.store.select(selectLayerOrder),
      this.store.select(selectRenderName),
    ),
    map(([_, layers, layerOrder, renderName]) => {
      const output: Partial<FirebaseRenderData> = {
        renderName,
        editorState:  {
          layerOrder,
          layers
        }
      }
      return output
    }),
    distinctUntilChanged((prev, curr) =>{
      return objectHash(prev) === objectHash(curr)
    }),
    pairwise(),
    map(([prev, curr]) => {
      const updateObj = firebaseDiffObjects(prev, curr)
      return updateObj
    })
  )

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<BaseState>,
    private httpClient: HttpClient,
    private imageDataService: RawImageDataLoaderService,
    private renderDataActionService: RenderDataActionsService,
    private firestoreService: FirebaseFirestoreService
  ) {
    this.loadRender$.subscribe(async (output) => {
      const loaded = await output
      this.loading = !loaded
    })
    this.syncSave.subscribe((output) => {
      this.firestoreService.syncUpdateGroupRenderData(output)
    })
    this.renderDataSnapshot$.pipe(takeUntilDestroyed()).subscribe(x => {
      if(!x || !x.editorState) {
        return
      }
      this.store.dispatch(setRender(
        {
          renderName: x.renderName,
          renderId: x.id,
          layerOrder: x.editorState.layerOrder,
          layers: x.editorState.layers,
        }
      ))
    })
    //this.store.dispatch(resetEditorState())
  }
}
